import { useLocation } from '@tanstack/react-router';
import type { VisibleSidebarSection } from '@portal/platform/configuration';
import { AppLink } from '../app-link';
import { useShellTexts } from '../i18n/use-shell-texts';
import { breadcrumbTrail, type Crumb } from './active-route';

/**
 * Where the page sits in the menu: "Serviceaftaler › Bestillinger", the section first so an entry
 * that two products share a name for ("Kunder", "Prisliste") is never ambiguous. Earlier entries
 * link, the section too when it has an overview page; the current page does not. A page outside
 * the menu links "Forside".
 */
export function Breadcrumb({ sections }: { sections: VisibleSidebarSection[] }) {
  const t = useShellTexts().breadcrumb;
  const { pathname } = useLocation();
  const trail = breadcrumbTrail(sections, pathname);
  // Outside the menu the page has no place to name; "Forside" is then just the way home.
  const crumbs: Crumb[] = trail.length > 0 ? trail : [{ label: t.home, route: '/' }];
  const current = trail.length > 0 ? crumbs.length - 1 : -1;
  return (
    <nav aria-label={t.label} className="px-gutter pt-4 pb-5">
      <ol className="flex flex-wrap items-center text-sm">
        {crumbs.map((crumb, index) => {
          return (
            <li key={`${index}-${crumb.label}`} className="flex items-center">
              {index > 0 ? (
                <span aria-hidden="true" className="px-2 text-ink-muted">
                  ›
                </span>
              ) : null}
              {index === current ? (
                <span aria-current="page" className="font-semibold text-ink">
                  {crumb.label}
                </span>
              ) : crumb.route ? (
                <AppLink
                  href={crumb.route}
                  className="text-brand no-underline hover:text-brand-dark hover:underline"
                >
                  {crumb.label}
                </AppLink>
              ) : (
                <span className="text-ink-muted">{crumb.label}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
